import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useSetPasswordMutation } from '../features/auth/authApi';
import InlineError from '../components/InlineError';

export default function SetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [password, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [success, setSuccess] = useState(false);
    const [clientError, setClientError] = useState('');
    const [setPasswordMutation, { isLoading, error }] = useSetPasswordMutation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    const getStrength = (pwd: string): { score: number; label: string; color: string } => {
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;
        const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
        const colors = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981'];
        return { score, label: labels[score] || '', color: colors[score] || '' };
    };

    const strength = getStrength(password);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setClientError('');

        if (password.length < 8) {
            setClientError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setClientError('Passwords do not match.');
            return;
        }

        try {
            await setPasswordMutation({ token, password }).unwrap();
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            // handled by InlineError
        }
    }

    if (success) {
        return (
            <div className="flex min-h-screen items-center justify-center px-4" style={{ background: '#0f172a' }}>
                <div className="text-center max-w-md">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(16,185,129,0.15)', border: '2px solid #10b981' }}>
                        <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Password Set!</h2>
                    <p className="text-blue-300 mb-6">Your account is ready. Redirecting you to login...</p>
                    <Link to="/login" className="text-blue-400 hover:text-white transition-colors text-sm">
                        Go to Login →
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>
            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-white">Set Your Password</h1>
                    <p className="mt-2 text-sm text-blue-300">
                        Welcome to noorHR! Create a secure password to get started.
                    </p>
                </div>

                <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <form onSubmit={onSubmit} className="space-y-5">
                        {/* Password */}
                        <div>
                            <label htmlFor="new-password" className="block text-sm font-medium text-blue-200 mb-2">
                                New Password
                            </label>
                            <div className="relative">
                                <input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                                    placeholder="Min. 8 characters"
                                    required
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-white transition-colors"
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>

                            {/* Password strength bar */}
                            {password.length > 0 && (
                                <div className="mt-2">
                                    <div className="flex gap-1 mb-1">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div
                                                key={i}
                                                className="h-1 flex-1 rounded-full transition-all duration-300"
                                                style={{ background: i <= strength.score ? strength.color : 'rgba(255,255,255,0.1)' }}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs" style={{ color: strength.color }}>
                                        {strength.label}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirm-password" className="block text-sm font-medium text-blue-200 mb-2">
                                Confirm Password
                            </label>
                            <input
                                id="confirm-password"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: `1px solid ${confirmPassword && confirmPassword !== password ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
                                }}
                                placeholder="Re-enter password"
                                required
                            />
                            {confirmPassword && confirmPassword !== password && (
                                <p className="mt-1 text-xs text-red-400">Passwords do not match</p>
                            )}
                        </div>

                        {/* Password requirements */}
                        <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <p className="text-xs text-blue-400 font-medium mb-2">Password requirements:</p>
                            <ul className="space-y-1">
                                {[
                                    { check: password.length >= 8, label: 'At least 8 characters' },
                                    { check: /[A-Z]/.test(password), label: 'One uppercase letter' },
                                    { check: /[0-9]/.test(password), label: 'One number' },
                                    { check: /[^A-Za-z0-9]/.test(password), label: 'One special character' },
                                ].map(({ check, label }) => (
                                    <li key={label} className="flex items-center gap-2 text-xs">
                                        <span style={{ color: check ? '#10b981' : 'rgba(255,255,255,0.3)' }}>
                                            {check ? '✓' : '○'}
                                        </span>
                                        <span style={{ color: check ? '#10b981' : 'rgba(255,255,255,0.4)' }}>{label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {clientError && <InlineError message={clientError} />}
                        <InlineError message={(error as any)?.data?.message || (error as any)?.error} />

                        <button
                            type="submit"
                            disabled={isLoading || password.length < 8 || password !== confirmPassword}
                            className="w-full py-3 px-4 rounded-xl font-semibold text-white text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                            style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}
                        >
                            {isLoading ? 'Setting password...' : 'Set Password & Continue'}
                        </button>
                    </form>
                </div>

                <p className="mt-6 text-center text-xs text-blue-500">
                    This link expires in 24 hours. Contact HR if it has expired.
                </p>
            </div>
        </div>
    );
}
